/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function() {
  var links = document.links;
  
  for (let i=0, linkslength = links.length; i < linkslength; i++) {        
    if (links[i].href === "https://jobs.illicebrandsgroup.com/") {
      console.log(links[i].href);
      links[i].target = "_blank";
    }
  }
})();

// Start nearby corporate photos and upcoming slides before they enter the viewport.
(() => {
  if (!('IntersectionObserver' in window) || navigator.connection?.saveData) return;

  const imageSelector = '.ibg-picture__image[loading="lazy"], .ibg-news-card__image[loading="lazy"]';
  const trackSelector = '[data-ibg-slider-track], .ibg-scroller';
  const lookAhead = 1800;
  const waitingImages = new Set();
  const tracks = new Map();
  const backgroundImages = new Set();
  const backgroundLoads = new Map();
  let backgroundReady = document.readyState === 'complete';
  let backgroundScheduled = false;
  let resizeFrame;

  const isVisibleBox = (box) => box.width > 0 && box.height > 0;
  const startImage = (image) => {
    if (!image.matches(imageSelector) || !isVisibleBox(image.getBoundingClientRect())) return;
    // Let <picture> and srcset select the source; do not fetch a second desktop image.
    image.loading = 'eager';
    observer.unobserve(image);
    waitingImages.delete(image);
    backgroundImages.delete(image);
  };

  const canWarmBackground = () => document.visibilityState === 'visible'
    && !navigator.connection?.saveData
    && !['slow-2g', '2g', '3g'].includes(navigator.connection?.effectiveType);

  const scheduleBackground = () => {
    if (!backgroundReady || backgroundScheduled || !backgroundImages.size
      || backgroundLoads.size >= 2 || !canWarmBackground()) return;
    backgroundScheduled = true;
    const run = () => {
      backgroundScheduled = false;
      if (!canWarmBackground()) return;
      for (const image of backgroundImages) {
        if (backgroundLoads.size >= 2) break;
        if (!image.isConnected || image.complete || !image.matches(imageSelector)) {
          backgroundImages.delete(image);
          continue;
        }
        if (!isVisibleBox(image.getBoundingClientRect())) continue;
        const release = () => {
          image.removeEventListener('load', release);
          image.removeEventListener('error', release);
          backgroundLoads.delete(image);
          scheduleBackground();
        };
        backgroundLoads.set(image, release);
        image.addEventListener('load', release, { once: true });
        image.addEventListener('error', release, { once: true });
        startImage(image);
        if (image.complete) release();
      }
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1000 });
    else window.setTimeout(run, 150);
  };

  const warmTrack = (track) => {
    const box = track.getBoundingClientRect();
    if (!isVisibleBox(box) || box.bottom < -600 || box.top > window.innerHeight + lookAhead) return;
    const nextPage = track.clientWidth;
    track.querySelectorAll(imageSelector).forEach((image) => {
      const imageBox = image.getBoundingClientRect();
      if (imageBox.right > box.left - nextPage && imageBox.left < box.right + nextPage) startImage(image);
    });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (tracks.has(entry.target)) warmTrack(entry.target);
      else startImage(entry.target);
    });
  }, { rootMargin: `600px 0px ${lookAhead}px 0px`, threshold: 0 });

  const watchTrack = (track) => {
    if (!tracks.has(track)) {
      const state = { frame: 0 };
      state.onScroll = () => {
        if (state.frame) return;
        state.frame = window.requestAnimationFrame(() => {
          state.frame = 0;
          warmTrack(track);
        });
      };
      tracks.set(track, state);
      track.addEventListener('scroll', state.onScroll, { passive: true });
      observer.observe(track);
    }
    warmTrack(track);
  };

  const initializeImages = (root = document) => {
    root.querySelectorAll(imageSelector).forEach((image) => {
      if (image.complete && image.naturalWidth > 0) return;
      backgroundImages.add(image);
      const track = image.closest(trackSelector);
      if (track) watchTrack(track);
      else if (!waitingImages.has(image)) {
        waitingImages.add(image);
        observer.observe(image);
      }
    });
    scheduleBackground();
  };

  document.addEventListener('shopify:section:load', (event) => initializeImages(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    backgroundImages.forEach((image) => {
      if (event.target.contains(image)) backgroundImages.delete(image);
    });
    backgroundLoads.forEach((release, image) => {
      if (event.target.contains(image)) release();
    });
    waitingImages.forEach((image) => {
      if (!event.target.contains(image)) return;
      observer.unobserve(image);
      waitingImages.delete(image);
    });
    tracks.forEach((state, track) => {
      if (!event.target.contains(track)) return;
      window.cancelAnimationFrame(state.frame);
      track.removeEventListener('scroll', state.onScroll);
      observer.unobserve(track);
      tracks.delete(track);
    });
  });
  window.addEventListener('resize', () => {
    if (resizeFrame) return;
    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = 0;
      tracks.forEach((state, track) => warmTrack(track));
      scheduleBackground();
    });
  }, { passive: true });

  window.addEventListener('load', () => {
    backgroundReady = true;
    scheduleBackground();
  }, { once: true });
  document.addEventListener('visibilitychange', scheduleBackground);
  navigator.connection?.addEventListener?.('change', scheduleBackground);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeImages(), { once: true });
  } else {
    initializeImages();
  }
})();
